import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { TrendingUp, BadgeCheck, Compass, ShieldCheck, Sparkles } from "lucide-react";
import Eyebrow from "./Eyebrow";

/**
 * Animated hub-and-spoke graphic for "Why Delcielo Tech".
 * Desktop: central node + 4 satellites at compass points, animated SVG paths with
 *          flowing dashes representing value/data converging to the brand.
 * Mobile : stacked timeline with a vertical animated path on the left edge.
 */
const ICONS = [TrendingUp, BadgeCheck, Compass, ShieldCheck];
const ACCENTS = ["amber", "cyan", "amber", "cyan"]; // alternate for balance

// Each satellite has its own position style (desktop) and a "side" for the curve.
const POSITIONS = [
  { name: "tl", className: "lg:col-start-1 lg:row-start-1", anchor: { x: 18, y: 22 } },
  { name: "tr", className: "lg:col-start-3 lg:row-start-1", anchor: { x: 82, y: 22 } },
  { name: "bl", className: "lg:col-start-1 lg:row-start-3", anchor: { x: 18, y: 78 } },
  { name: "br", className: "lg:col-start-3 lg:row-start-3", anchor: { x: 82, y: 78 } },
];

// SVG curve from each anchor to center (50,50)
function pathFrom({ x, y }) {
  // Add a slight bezier control between for a natural arc
  const cx = (x + 50) / 2;
  const cy = (y + 50) / 2 + (y < 50 ? -6 : 6);
  return `M ${x} ${y} Q ${cx} ${cy} 50 50`;
}

export default function WhyUsHub() {
  const { t } = useTranslation();
  const whys = t("home.why.items", { returnObjects: true });

  return (
    <section className="section-y relative overflow-hidden">
      {/* faint background grid + glow */}
      <div className="absolute inset-0 -z-10 bg-grid opacity-60" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl opacity-30 pointer-events-none"
           style={{
             background:
               "radial-gradient(circle, rgba(0,212,255,0.18) 0%, rgba(255,145,77,0.10) 40%, transparent 70%)",
           }} />

      <div className="container-page relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-3xl mb-16 lg:mb-24"
        >
          <Eyebrow className="mb-4">{t("home.why.eyebrow")}</Eyebrow>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            {t("home.why.title")}
          </h2>
        </motion.div>

        {/* ======= DESKTOP: hub-and-spoke ======= */}
        <div className="hidden lg:block">
          <div className="relative aspect-[16/10] xl:aspect-[16/9] w-full max-w-6xl mx-auto">

            {/* SVG paths overlay */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              <defs>
                <linearGradient id="lineCyan" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#00D4FF" stopOpacity="0" />
                  <stop offset="40%" stopColor="#00D4FF" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.9" />
                </linearGradient>
                <linearGradient id="lineAmber" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FF914D" stopOpacity="0" />
                  <stop offset="40%" stopColor="#FF914D" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#FF914D" stopOpacity="0.9" />
                </linearGradient>
              </defs>
              {POSITIONS.map((pos, i) => {
                const d = pathFrom(pos.anchor);
                const stroke = ACCENTS[i] === "amber" ? "url(#lineAmber)" : "url(#lineCyan)";
                const dotColor = ACCENTS[i] === "amber" ? "#FF914D" : "#00D4FF";
                return (
                  <g key={pos.name}>
                    {/* static guide */}
                    <motion.path
                      d={d}
                      stroke={stroke}
                      strokeWidth="0.35"
                      fill="none"
                      strokeLinecap="round"
                      initial={{ pathLength: 0, opacity: 0 }}
                      whileInView={{ pathLength: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, delay: 0.2 + i * 0.18, ease: "easeOut" }}
                    />
                    {/* dashed traveling overlay */}
                    <motion.path
                      d={d}
                      stroke={dotColor}
                      strokeWidth="0.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray="1 3"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 0.9 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 1.4 + i * 0.18 }}
                      style={{
                        animation: `flow-${i} ${2 + i * 0.3}s linear ${1.5 + i * 0.18}s infinite`,
                      }}
                    />
                  </g>
                );
              })}
            </svg>

            {/* Grid container for 3 cols x 3 rows */}
            <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-4">
              {/* Satellite cards */}
              {whys.map((w, i) => {
                const Icon = ICONS[i] || Sparkles;
                const accent = ACCENTS[i];
                const accentText = accent === "amber"
                  ? "text-dc-amber-light dark:text-dc-amber"
                  : "text-dc-cyan-light dark:text-dc-cyan";
                const accentBg = accent === "amber"
                  ? "bg-dc-amber-light/10 dark:bg-dc-amber/15"
                  : "bg-dc-cyan-light/10 dark:bg-dc-cyan/15";
                const accentRing = accent === "amber"
                  ? "ring-dc-amber-light/30 dark:ring-dc-amber/40"
                  : "ring-dc-cyan-light/30 dark:ring-dc-cyan/40";
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.85 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: 1.0 + i * 0.18, ease: "easeOut" }}
                    className={`${POSITIONS[i].className} self-center justify-self-center w-full max-w-[260px] glass-card p-5 ring-1 ${accentRing}`}
                  >
                    <div className={`w-10 h-10 rounded-xl ${accentBg} ${accentText} flex items-center justify-center mb-3`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className={`font-semibold mb-1.5 uppercase text-xs tracking-[0.15em] ${accentText}`}>{w.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{w.description}</p>
                  </motion.div>
                );
              })}

              {/* CENTER NODE */}
              <motion.div
                initial={{ opacity: 0, scale: 0.6 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: "backOut" }}
                className="col-start-2 row-start-2 self-center justify-self-center relative"
              >
                {/* Pulsing rings */}
                <span className="absolute inset-0 -m-2 rounded-full border border-dc-cyan/30 dark:border-dc-cyan/40 animate-ping opacity-70" />
                <span className="absolute inset-0 -m-6 rounded-full border border-dc-amber/20 dark:border-dc-amber/30 animate-ping opacity-50" style={{ animationDelay: "1s" }} />
                <div className="relative w-32 h-32 xl:w-36 xl:h-36 rounded-full bg-dc-navy dark:bg-dc-navy flex items-center justify-center shadow-2xl ring-4 ring-white dark:ring-dc-navy-card">
                  <div className="text-center">
                    <div className="font-heading text-xl xl:text-2xl font-bold text-white tracking-tight">
                      dc<span className="text-dc-cyan">.</span>
                    </div>
                    <div className="text-[10px] font-semibold tracking-[0.3em] text-white/70 -mt-1">TECH</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Inline keyframes for the flowing dashed lines */}
          <style>{`
            @keyframes flow-0 { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -20; } }
            @keyframes flow-1 { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -20; } }
            @keyframes flow-2 { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -20; } }
            @keyframes flow-3 { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -20; } }
          `}</style>
        </div>

        {/* ======= MOBILE: vertical timeline ======= */}
        <div className="lg:hidden relative">
          {/* center brand pill at top */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-10"
          >
            <div className="relative">
              <span className="absolute inset-0 -m-2 rounded-full border border-dc-cyan/40 animate-ping opacity-70" />
              <div className="relative w-24 h-24 rounded-full bg-dc-navy flex items-center justify-center shadow-xl ring-4 ring-white dark:ring-dc-navy-card">
                <div className="text-center">
                  <div className="font-heading text-lg font-bold text-white">dc<span className="text-dc-cyan">.</span></div>
                  <div className="text-[9px] tracking-[0.3em] text-white/70 -mt-1 font-semibold">TECH</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* vertical line */}
          <div className="absolute left-6 top-32 bottom-4 w-px bg-gradient-to-b from-dc-cyan/40 via-dc-amber/30 to-transparent" />

          <div className="space-y-5 pl-16">
            {whys.map((w, i) => {
              const Icon = ICONS[i] || Sparkles;
              const accent = ACCENTS[i];
              const accentText = accent === "amber" ? "text-dc-amber-light dark:text-dc-amber" : "text-dc-cyan-light dark:text-dc-cyan";
              const accentBg = accent === "amber" ? "bg-dc-amber-light/10 dark:bg-dc-amber/15" : "bg-dc-cyan-light/10 dark:bg-dc-cyan/15";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="relative"
                >
                  {/* node on the line */}
                  <div className={`absolute -left-[42px] top-3 w-3 h-3 rounded-full ${accent === "amber" ? "bg-dc-amber" : "bg-dc-cyan"} ring-4 ring-background`} />
                  <div className="glass-card p-5">
                    <div className={`w-10 h-10 rounded-xl ${accentBg} ${accentText} flex items-center justify-center mb-3`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className={`font-semibold mb-1.5 uppercase text-xs tracking-[0.15em] ${accentText}`}>{w.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{w.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
